import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES, isExcludedFromBestBook } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import AddToScratchpadLink from './AddToScratchpadLink';
import ModalBrandingBar from './ModalBrandingBar';
import PlayerStatsChart from './PlayerStatsChart';
import { getPrimaryLinePoint } from '../utils/gameLogChartConfig';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NFLPlayerModal = ({
  show,
  onHide,
  playerName,
  playerTeam,
  playerPosition,
  playerId,
  gameId,
  market,
  awayTeam,
  homeTeam,
  getTeamLogoUrl,
  getBookLogoUrl,
  onOddsClick,
  onRemoveBet,
  betslipItems = [],
  bookmakers = [],
  onToggleScratchpad,
  isInScratchpad,
  recalcEdgePlayer = false,
}) => {
  const { isBookEnabledForGraphs } = useBooks();
  // Map API market names to modal market types
  const getInitialMarketType = (apiMarket) => {
    if (!apiMarket) return 'touchdown';

    const marketMap = {
      'player_pass_yds': 'payds',
      'player_pass_tds': 'patds',
      'player_rush_yds': 'ruyds',
      'player_rush_attempts': 'ruatt',
      'player_reception_yds': 'reyds',
      'player_receptions': 'rec',
      'player_rush_reception_yds': 'rureyds',
      'player_field_goals': 'fg',
      'player_1st_td': '1stlast',
      'player_last_td': '1stlast'
    };

    return marketMap[apiMarket] || 'touchdown';
  };

  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMarketType, setSelectedMarketType] = useState(getInitialMarketType(market));

  // Skill-position players (QB/RB/TE/WR/FB) show every stat toggle even when
  // the book has no odds for that market — the game-log chart below is still
  // useful on its own. Kickers/defenders stay gated by hasData so they don't
  // see irrelevant toggles.
  const isSkillPosition = !!playerPosition &&
    ['QB', 'RB', 'TE', 'WR', 'FB'].includes(String(playerPosition).toUpperCase());
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipData, setTooltipData] = useState(null);

  // Chart state
  const [showChart, setShowChart] = useState(false);
  const [chartMarket, setChartMarket] = useState(null);
  const [chartPoint, setChartPoint] = useState(null);
  const [chartIsOver, setChartIsOver] = useState(true);
  const [oddsHistoryData, setOddsHistoryData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Over/Under toggle state
  const [showOver, setShowOver] = useState(true);
  const [showUnder, setShowUnder] = useState(false);

  // Helper to get the actual data key for a bookmaker
  // Note: API now returns data with display names (e.g., "caesars" not "williamhill_us")
  // so we just return the book name directly
  const getBookmakerDataKey = (book) => {
    return book;
  };

  // Filter a line.books map down to the books the user has enabled when
  // recalcEdgePlayer is on. Without recalc, returns every book present on
  // the line (matches the older always-all behavior).
  const getScopedBooks = (bookMap) => {
    if (!bookMap) return [];
    if (recalcEdgePlayer && bookmakers.length > 0) {
      return bookmakers
        .map(bk => bookMap[getBookmakerDataKey(bk)])
        .filter(Boolean);
    }
    return Object.values(bookMap);
  };

  // Render one <tr> for a single side of a line (over or under), reused by
  // the passing / rushing / receiving branches so the Under toggle wires up
  // consistently across all of them. Uses the standard tier palette
  // (green+yellow / amber+navy / blue+white) that the main odds screen and
  // desktop player panel use.
  //
  // pointCell:
  //   - { rowSpan: 2 }  → render the point column and let it span this row
  //                       plus the sibling Under row (used by the Over row
  //                       when both sides are visible, matches desktop).
  //   - { rowSpan: 1 }  → render the point column only in this row.
  //   - null            → skip the point column entirely (Under row when the
  //                       Over row above already carries a rowSpan=2 cell).
  const renderOddsRow = (line, side, marketKey, keyPrefix, lineIndex, pointCell = { rowSpan: 1 }) => {
    const isOver = side === 'over';
    return (
      <tr key={`${keyPrefix}-${side}-${lineIndex}-${line.point}`}>
        {pointCell && (
          <td
            rowSpan={pointCell.rowSpan || 1}
            style={{ fontWeight: 'bold', textAlign: 'center', color: '#007bff', cursor: 'pointer', verticalAlign: 'middle' }}
            onClick={() => fetchOddsHistory(marketKey, line.point, isOver)}
          >
            {line.point}
          </td>
        )}
        {bookmakers.map(book => {
          const dataKey = getBookmakerDataKey(book);
          const odds = line.books?.[dataKey]?.[side];
          const lineOdds = line.books
            ? getScopedBooks(line.books).map(b => b[side]).filter(o => o !== null && o !== undefined)
            : [];
          const bestOdds = lineOdds.length ? Math.max(...lineOdds) : null;
          const secondBestOdds = lineOdds.length > 1
            ? Math.max(...lineOdds.filter(o => o !== bestOdds))
            : null;
          let diff = 0;
          if (bestOdds !== null && secondBestOdds !== null) {
            if (bestOdds > 0 && secondBestOdds < 0) {
              const spreadPositive = bestOdds - 100;
              const spreadNegative = secondBestOdds - (-100);
              diff = Math.abs((spreadPositive - spreadNegative) / secondBestOdds);
            } else {
              diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
            }
          }
          const bestOddsCount = lineOdds.filter(o => o === bestOdds).length;
          const isBest = bestOdds !== null && odds === bestOdds && bestOddsCount === 1;

          const cellStyle = {
            cursor: odds ? 'pointer' : 'default',
            fontWeight: 'bold',
            textAlign: 'center',
            minWidth: '70px',
          };
          if (isBest && lineOdds.length > 1) {
            if (diff >= 0.25) Object.assign(cellStyle, { backgroundColor: 'green', color: 'yellow', borderRadius: '3px' });
            else if (diff >= 0.15) Object.assign(cellStyle, { backgroundColor: '#F59E0B', color: '#1E40AF', borderRadius: '3px' });
            else if (diff >= 0.05) Object.assign(cellStyle, { backgroundColor: '#3B82F6', color: 'white', borderRadius: '3px' });
            else Object.assign(cellStyle, { color: '#F59E0B' });
          }
          if (line.books?.[dataKey]?.[`${side}_isAvailable`] === false) {
            cellStyle.opacity = 0.25;
          }

          const betMeta = {
            gameId, playerId, playerName, marketKey, book, odds,
            awayTeam, homeTeam, playerTeam, playerPosition,
            line: line.point, side,
          };

          return (
            <td
              key={`${book}-${side}`}
              style={cellStyle}
              onClick={(e) => {
                e.stopPropagation();
                if (odds) handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                if (!isMobile && odds && onOddsClick) onOddsClick(gameId, playerId, playerName, marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, side);
              }}
              onMouseEnter={(e) => {
                if (!isMobile && odds) handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
              }}
              onMouseLeave={() => {
                if (!isMobile) setTooltipData(null);
              }}
            >
              {formatOdds(odds)}
            </td>
          );
        })}
      </tr>
    );
  };

  // Helper function to process flat market lines from new endpoint structure
  const processMarketLines = (marketLines, marketKey) => {
    if (!marketLines || !Array.isArray(marketLines)) return [];

    // New flat structure - just transform to modal format
    return marketLines.map(line => {
      // Convert flat structure to books object
      const books = {};

      // Extract all book data from the line
      Object.keys(line).forEach(key => {
        if (key.startsWith(`${marketKey}_`) && !key.includes('bestBook') && !key.includes('nextBook')) {
          const suffix = key.replace(`${marketKey}_`, '');
          const parts = suffix.split('_');

          if (parts.length >= 2) {
            const bookName = parts.slice(0, -1).join('_');
            const field = parts[parts.length - 1];

            if (field === 'over' || field === 'under' || field === 'yes' || field === 'no' || field === 'point' || field === 'isAlternate' || field === 'isAvailable') {
              if (!books[bookName]) {
                books[bookName] = {};
              }
              books[bookName][field] = line[key];
            }
            // Also capture isAvailable with _over or _under suffix
            if (field === 'over' || field === 'under') {
              const availKey = `${marketKey}_${bookName}_${field}_isAvailable`;
              if (line[availKey] !== undefined) {
                books[bookName][`${field}_isAvailable`] = line[availKey];
              }
            }
          }
        }
      });

      return {
        point: line.point,
        books: books,
        bestBook: line[`${marketKey}_bestBook`],
        bestBookPrice: line[`${marketKey}_bestBookPrice`],
        bestBookDiff: line[`${marketKey}_bestBookDiff`]
      };
    }).sort((a, b) => (a.point || 0) - (b.point || 0));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Defensive cleanup for React-Bootstrap Modal close. If a transitionend
  // event is dropped mid-close (has happened on iOS Safari when custom
  // scrollable + fullscreen CSS collides), Bootstrap leaves the .modal-open
  // class + inline body styles + an orphan .modal-backdrop attached to
  // document.body. The FAB (z-index 1040) then sits behind that orphan
  // backdrop and can't receive clicks. Force-remove them after show goes
  // false to guarantee the page is interactive again.
  useEffect(() => {
    if (show) return;
    // Also clear any stale tooltip/sheet state so it doesn't flash on next open.
    setTooltipData(null);
    const t = setTimeout(() => {
      const stray = document.querySelectorAll('.modal-backdrop, .player-modal-backdrop');
      stray.forEach(el => el.parentNode && el.parentNode.removeChild(el));
      document.body.classList.remove('modal-open');
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
      if (document.body.style.paddingRight) document.body.style.paddingRight = '';
    }, 350); // after Bootstrap's fade-out (default ~150ms) + safety margin
    return () => clearTimeout(t);
  }, [show]);

  // Fetch player data when modal opens
  useEffect(() => {
    if (show && playerId && gameId) {
      fetchPlayerData();
      // Set market type based on the market prop
      setSelectedMarketType(getInitialMarketType(market));
    } else if (!show) {
      // Reset state when modal closes
      setPlayerData(null);
      setError(null);
      setSelectedMarketType(getInitialMarketType(market));
      setShowChart(false);
      setChartMarket(null);
      setChartPoint(null);
      setChartIsOver(true);
      setOddsHistoryData(null);
      setSelectedTier(null);
    }
  }, [show, playerId, gameId, market]);

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    if (!odds || odds === '-') return null;
    const numericOdds = parseInt(odds);
    if (isNaN(numericOdds)) return null;

    if (numericOdds > 0) {
      return (100 / (numericOdds + 100)) * 100;
    } else {
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Handle odds cell click to show tooltip
  const handleOddsClick = (event, odds, book, point, bestBook, bestBookDiff, lineBooks, betMeta = null) => {
    if (!odds || odds === '-') return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Calculate edge using the SAME logic as tier styling for consistency
    let edge = 0;
    if (lineBooks) {
      const allOdds = getScopedBooks(lineBooks)
        .map(b => b.over || b.yes)
        .filter(o => o !== null && o !== undefined);

      if (allOdds.length >= 2) {
        const bestOdds = Math.max(...allOdds);
        const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));
        const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
        const isBest = odds === bestOdds && bestOddsCount === 1;

        if (isBest) {
          // Handle cross-zero comparison (positive best odds vs negative second best)
          let diff;
          if (bestOdds > 0 && secondBestOdds < 0) {
            const spreadPositive = bestOdds - 100;
            const spreadNegative = secondBestOdds - (-100);
            const totalSpread = spreadPositive - spreadNegative;
            diff = Math.abs(totalSpread / secondBestOdds);
          } else {
            diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
          }
          edge = diff * 100;
        }
      }
    }

    let x, y;
    if (isMobile) {
      x = window.innerWidth / 2 - 100;
      y = Math.min(event.clientY, window.innerHeight - 150);
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltipData({
      odds,
      book,
      point,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2),
      x,
      y,
      // Full metadata for the mobile bottom-sheet's Add / Remove buttons.
      // Cell click passes this in so the sheet has everything it needs to
      // add the bet (via onOddsClick) or match it against betslipItems for
      // the Remove state.
      meta: betMeta,
    });
  };

  // Desktop tooltip closes on any outside click. On mobile the info now
  // renders as a bottom sheet with an explicit close X, so we intentionally
  // don't auto-dismiss on outside taps — tapping a different odds cell
  // simply replaces the sheet's contents.
  useEffect(() => {
    if (!isMobile && tooltipData) {
      const handleClickOutside = () => setTooltipData(null);
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [tooltipData, isMobile]);

  // Fetch odds history for a specific market and tier
  const fetchOddsHistory = async (marketKey, point = null, isOver = true) => {
    if (!gameId || !playerId || !marketKey) {
      console.error('Missing required parameters for odds history');
      return;
    }

    setLoadingChart(true);
    setError(null);
    try {
      const response = await fetch(`/api/games/nfl/${gameId}/${playerId}/${marketKey}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setOddsHistoryData(data);
      setSelectedTier(point);
      setShowChart(true);
      setChartMarket(marketKey);
      setChartPoint(point);
      setChartIsOver(isOver);
    } catch (err) {
      console.error('Error fetching odds history:', err);
      setError(err.message);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleBackToTable = () => {
    setShowChart(false);
    setChartMarket(null);
    setChartPoint(null);
    setChartIsOver(true);
    setOddsHistoryData(null);
    setSelectedTier(null);
  };

  const fetchPlayerData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Find player ID - need to get this from the parent component or fetch it
      // For now, we'll need to pass playerId as a prop
      const response = await fetch(`/api/games/nfl/${gameId}/${playerId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch player data');
      }

      const data = await response.json();

      // Transform the API data to our component format
      const transformedData = {
        player: {
          name: data.playerName,
          team: data.team,
          position: playerPosition,
          photo: `https://a.espncdn.com/i/headshots/nfl/players/full/${playerId?.replace('nfl_', '')}.png`,
          jerseyNumber: null // Not in API response
        },
        markets: []
      };

      // Create main touchdowns market (Anytime + Multiple TDs) - new flat structure
      const mainTdMarket = {
        marketKey: 'player_main_tds',
        marketName: 'Touchdown Scorer',
        category: 'touchdown',
        lines: []
      };

      // Check different possible field names for multiple TDs
      let actualTdMarketKey = null;
      let multiTdData = null;

      if (data.player_tds_over && Array.isArray(data.player_tds_over)) {
        actualTdMarketKey = 'player_tds_over';
        multiTdData = data.player_tds_over;
      } else if (data.player_tds && Array.isArray(data.player_tds)) {
        actualTdMarketKey = 'player_tds';
        multiTdData = data.player_tds;
      } else if (data.player_touchdowns_over && Array.isArray(data.player_touchdowns_over)) {
        actualTdMarketKey = 'player_touchdowns_over';
        multiTdData = data.player_touchdowns_over;
      } else if (data.player_total_tds && Array.isArray(data.player_total_tds)) {
        actualTdMarketKey = 'player_total_tds';
        multiTdData = data.player_total_tds;
      }

      if (multiTdData && actualTdMarketKey) {
        const lines = processMarketLines(multiTdData, actualTdMarketKey);

        // Add labels and market keys
        lines.forEach(line => {
          const adjustedLabel = `${Math.ceil(line.point)}+`;
          const historyMarketKey = line.point === 0.5 ? 'player_anytime_td' : actualTdMarketKey;

          mainTdMarket.lines.push({
            ...line,
            label: adjustedLabel,
            actualMarketKey: historyMarketKey
          });
        });
      }

      // Only add main market if it has lines
      if (mainTdMarket.lines.length > 0) {
        transformedData.markets.push(mainTdMarket);
      }

      // Create 1st/Last TD market
      const firstLastTdMarket = {
        marketKey: 'player_first_last_tds',
        marketName: '1st / Last Touchdown',
        category: 'firstlast',
        lines: []
      };

      // Add 1st TD - array with single element in new structure
      if (data.player_1st_td && Array.isArray(data.player_1st_td) && data.player_1st_td.length > 0) {
        const firstTdLine = data.player_1st_td[0];
        const books = {};
        let bestBook = null;
        let bestBookPrice = null;
        let bestBookDiff = null;

        // Extract all keys from the line object. Only accept keys whose value
        // is a number — the payload also carries per-book metadata suffixes
        // like `_isAvailable`, `_isFallback`, `_fallbackSource` that would
        // otherwise be treated as phantom books and contaminate Math.max in
        // the tier calc (NaN → nothing highlights).
        Object.keys(firstTdLine).forEach(key => {
          if (key === 'player_1st_td_bestBook') {
            bestBook = firstTdLine[key];
          } else if (key === 'player_1st_td_bestBookPrice') {
            bestBookPrice = firstTdLine[key];
          } else if (key === 'player_1st_td_bestBookDiff') {
            bestBookDiff = firstTdLine[key];
          } else if (key.startsWith('player_1st_td_') &&
                     typeof firstTdLine[key] === 'number') {
            const bookName = key.replace('player_1st_td_', '');
            if (!books[bookName]) {
              books[bookName] = {};
            }
            books[bookName].yes = firstTdLine[key];
          } else if (key.startsWith('player_1st_td_') && key.endsWith('_isAvailable')) {
            // Preserve availability flags on the corresponding book entry so
            // the render can dim unavailable cells (see yes_isAvailable check
            // in the render path).
            const bookName = key.replace('player_1st_td_', '').replace('_isAvailable', '');
            if (!books[bookName]) books[bookName] = {};
            books[bookName].yes_isAvailable = firstTdLine[key];
          }
        });

        if (Object.keys(books).length > 0) {
          firstLastTdMarket.lines.push({
            point: '1st',
            label: '1st TD',
            books: books,
            bestBook: bestBook,
            bestBookPrice: bestBookPrice,
            bestBookDiff: bestBookDiff,
            actualMarketKey: 'player_1st_td'
          });
        }
      }

      // Add last TD - array with single element in new structure
      if (data.player_last_td && Array.isArray(data.player_last_td) && data.player_last_td.length > 0) {
        const lastTdLine = data.player_last_td[0];
        const books = {};
        let bestBook = null;
        let bestBookPrice = null;
        let bestBookDiff = null;

        // Extract all keys — see the parallel parser above for 1st TD; same
        // number-only guard prevents metadata suffixes from being treated as
        // phantom books.
        Object.keys(lastTdLine).forEach(key => {
          if (key === 'player_last_td_bestBook') {
            bestBook = lastTdLine[key];
          } else if (key === 'player_last_td_bestBookPrice') {
            bestBookPrice = lastTdLine[key];
          } else if (key === 'player_last_td_bestBookDiff') {
            bestBookDiff = lastTdLine[key];
          } else if (key.startsWith('player_last_td_') &&
                     typeof lastTdLine[key] === 'number') {
            const bookName = key.replace('player_last_td_', '');
            if (!books[bookName]) {
              books[bookName] = {};
            }
            books[bookName].yes = lastTdLine[key];
          } else if (key.startsWith('player_last_td_') && key.endsWith('_isAvailable')) {
            const bookName = key.replace('player_last_td_', '').replace('_isAvailable', '');
            if (!books[bookName]) books[bookName] = {};
            books[bookName].yes_isAvailable = lastTdLine[key];
          }
        });

        if (Object.keys(books).length > 0) {
          firstLastTdMarket.lines.push({
            point: 'last',
            label: 'Last TD',
            books: books,
            bestBook: bestBook,
            bestBookPrice: bestBookPrice,
            bestBookDiff: bestBookDiff,
            actualMarketKey: 'player_last_td'
          });
        }
      }

      // Only add first/last market if it has lines
      if (firstLastTdMarket.lines.length > 0) {
        transformedData.markets.push(firstLastTdMarket);
      }

      // Process passing touchdowns - new flat structure
      if (data.player_pass_tds && Array.isArray(data.player_pass_tds)) {
        const lines = processMarketLines(data.player_pass_tds, 'player_pass_tds');

        // Add labels for display (1+, 2+, etc.)
        const linesWithLabels = lines.map(line => ({
          ...line,
          label: `${Math.ceil(line.point)}+`
        }));

        if (linesWithLabels.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_pass_tds',
            marketName: 'Passing Touchdowns',
            category: 'passing',
            subCategory: 'PaTDS',
            lines: linesWithLabels
          });
        }
      }

      // Process passing yards - new flat structure
      if (data.player_pass_yds && Array.isArray(data.player_pass_yds)) {
        const lines = processMarketLines(data.player_pass_yds, 'player_pass_yds');

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_pass_yds',
            marketName: 'Passing Yards',
            category: 'passing',
            subCategory: 'PaYds',
            lines: lines
          });
        }
      }

      // Process passing attempts - separate sub-category
      if (data.player_pass_attempts?.over_under) {
        const lines = data.player_pass_attempts.over_under.map(line => ({
          point: line.point,
          books: line.books || {},
          bestBook: line.bestBook,
          bestBookPrice: line.bestBookPrice,
          bestBookDiff: line.bestBookDiff
        }));
        transformedData.markets.push({
          marketKey: 'player_pass_attempts',
          marketName: 'Pass Attempts',
          category: 'passing',
          subCategory: 'PaAttempts',
          lines
        });
      }

      // Process passing completions - separate sub-category
      if (data.player_pass_completions?.over_under) {
        const lines = data.player_pass_completions.over_under.map(line => ({
          point: line.point,
          books: line.books || {},
          bestBook: line.bestBook,
          bestBookPrice: line.bestBookPrice,
          bestBookDiff: line.bestBookDiff
        }));
        transformedData.markets.push({
          marketKey: 'player_pass_completions',
          marketName: 'Completions',
          category: 'passing',
          subCategory: 'PaCompletions',
          lines
        });
      }

      // Process rushing yards - new flat structure
      if (data.player_rush_yds && Array.isArray(data.player_rush_yds)) {
        const lines = processMarketLines(data.player_rush_yds, 'player_rush_yds');

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_rush_yds',
            marketName: 'Rushing Yards',
            category: 'rushing',
            subCategory: 'RuYds',
            lines: lines
          });
        }
      }

      // Process rushing attempts - handle both flat array and object formats
      if (data.player_rush_attempts) {
        let lines = [];

        // Check if it's a flat array (new format like yards markets)
        if (Array.isArray(data.player_rush_attempts)) {
          lines = processMarketLines(data.player_rush_attempts, 'player_rush_attempts');
        } else {
          // Handle old object format with over_under/alternates
          const allRushAttLines = [];

          if (data.player_rush_attempts.over_under) {
            allRushAttLines.push(...data.player_rush_attempts.over_under);
          }

          if (data.player_rush_attempts.alternates) {
            allRushAttLines.push(...data.player_rush_attempts.alternates);
          }

          const uniqueLines = [];
          const seenPoints = new Set();

          allRushAttLines
            .sort((a, b) => a.point - b.point)
            .forEach(line => {
              if (!seenPoints.has(line.point)) {
                seenPoints.add(line.point);
                uniqueLines.push({
                  point: line.point,
                  books: line.books || {},
                  bestBook: line.bestBook,
                  bestBookPrice: line.bestBookPrice,
                  bestBookDiff: line.bestBookDiff
                });
              }
            });

          lines = uniqueLines;
        }

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_rush_attempts',
            marketName: 'Rush Attempts',
            category: 'rushing',
            subCategory: 'RuATT',
            lines: lines
          });
        }
      }

      // Process receiving yards - new flat structure
      if (data.player_reception_yds && Array.isArray(data.player_reception_yds)) {
        const lines = processMarketLines(data.player_reception_yds, 'player_reception_yds');

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_reception_yds',
            marketName: 'Receiving Yards',
            category: 'receiving',
            subCategory: 'ReYds',
            lines: lines
          });
        }
      }

      // Process receptions - new flat structure
      if (data.player_receptions && Array.isArray(data.player_receptions)) {
        const lines = processMarketLines(data.player_receptions, 'player_receptions');

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_receptions',
            marketName: 'Receptions',
            category: 'receiving',
            subCategory: 'Receptions',
            lines: lines
          });
        }
      }

      // Process rushing + receiving yards (combo) - new flat structure
      if (data.player_rush_reception_yds && Array.isArray(data.player_rush_reception_yds)) {
        const lines = processMarketLines(data.player_rush_reception_yds, 'player_rush_reception_yds');

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_rush_reception_yds',
            marketName: 'Rush + Rec Yards',
            category: 'rushing_receiving',
            subCategory: 'RuReYds',
            lines: lines
          });
        }
      }

      // Process field goals - new flat structure
      if (data.player_field_goals && Array.isArray(data.player_field_goals)) {
        const lines = processMarketLines(data.player_field_goals, 'player_field_goals');

        if (lines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_field_goals',
            marketName: 'Field Goals Made',
            category: 'kicking',
            subCategory: 'FG',
            lines: lines
          });
        }
      }

      setPlayerData(transformedData);
    } catch (err) {
      console.error('Error fetching player data:', err);
      setError('Failed to load player data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!oddsHistoryData || !oddsHistoryData.oddsHistory || oddsHistoryData.oddsHistory.length === 0) {
      console.log('No odds history data available');
      return null;
    }

    console.log('NFL Chart Data Debug:', {
      chartMarket,
      selectedTier,
      totalEntries: oddsHistoryData.oddsHistory.length,
      sampleEntry: oddsHistoryData.oddsHistory[0],
      uniqueMarketTypes: [...new Set(oddsHistoryData.oddsHistory.map(e => e.marketType))],
      uniquePoints: [...new Set(oddsHistoryData.oddsHistory.map(e => e.point))]
    });

    // Filter by market key and point value
    // For special markets (1st TD, Last TD), selectedTier will be null
    // For over/under markets, filter by the selected point value
    // Use tolerance for floating point comparison
    const filteredHistory = oddsHistoryData.oddsHistory.filter(entry => {
      // First, filter by market key (API uses "marketType" field)
      if (entry.marketType !== chartMarket) return false;

      // Then filter by point value if applicable
      if (selectedTier !== null) {
        const pointMatch = Math.abs(entry.point - selectedTier) < 0.01;
        return pointMatch;
      }

      return true;
    });

    console.log('Filtered entries:', filteredHistory.length);

    if (filteredHistory.length === 0) {
      console.log('No filtered entries found');
      return null;
    }

    // Get all unique bookmakers from the filtered data
    const allBookmakers = new Set();
    filteredHistory.forEach(entry => {
      if (entry.books && typeof entry.books === 'object') {
        Object.keys(entry.books).forEach(book => {
          if (entry.books[book]) {
            if (entry.books[book].over !== undefined ||
                entry.books[book].yes !== undefined ||
                typeof entry.books[book] === 'number') {
              allBookmakers.add(book);
            }
          }
        });
      }
    });

    // Include every book; Settings → Graphs only sets the initial hidden flag below.
    const bookmakersArray = Array.from(allBookmakers);

    if (bookmakersArray.length === 0) return null;

    // Group entries by timestamp to handle multiple markets (main + alt) at same time
    // Normalize timestamps to nearest 5 minutes to combine entries that are close in time
    const groupedByTimestamp = {};
    filteredHistory.forEach(entry => {
      if (!entry.timestamp) return;
      const date = new Date(entry.timestamp);
      // Round to nearest 5 minutes
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      date.setMinutes(roundedMinutes);
      date.setSeconds(0);
      date.setMilliseconds(0);
      const normalizedTimestamp = date.toISOString();

      if (!groupedByTimestamp[normalizedTimestamp]) {
        groupedByTimestamp[normalizedTimestamp] = [];
      }
      groupedByTimestamp[normalizedTimestamp].push(entry);
    });

    const timestamps = Object.keys(groupedByTimestamp).sort((a, b) => new Date(a) - new Date(b));

    if (timestamps.length === 0) return null;

    // Prepare labels (timestamps) - use unique timestamps
    const labels = timestamps.map(timestamp => {
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return 'Invalid Date';
      }
    });

    // Color palette for bookmakers
    const bookmakerColors = {
      fanduel: '#1A3D6C',
      draftkings: '#5DD335',
      betmgm: '#FFCC00',
      fanatics: '#002F6C',
      espnbet: '#FF0033',
      betrivers: '#EC1C24',
      ballybet: '#000000',
      bovada: '#CC0000',
      caesars: '#000000',
      hardrockbet: '#D4145A'
    };

    // Prepare datasets for each bookmaker
    const datasetsWithLatestValues = bookmakersArray
      .map(bookmaker => {
        const data = timestamps.map(timestamp => {
          const entries = groupedByTimestamp[timestamp];
          const values = [];

          entries.forEach(entry => {
            if (entry.books && entry.books[bookmaker]) {
              let value;

              // For special markets (1st TD, Last TD), use 'yes' or direct value
              if (selectedTier === null) {
                if (entry.books[bookmaker].yes !== undefined) {
                  value = parseFloat(entry.books[bookmaker].yes);
                } else if (entry.books[bookmaker].over !== undefined) {
                  value = parseFloat(entry.books[bookmaker].over);
                } else if (typeof entry.books[bookmaker] === 'number') {
                  value = parseFloat(entry.books[bookmaker]);
                }
              } else {
                // For over/under markets, use chartIsOver to determine which odds to show
                if (chartIsOver) {
                  if (entry.books[bookmaker].over !== undefined) {
                    value = parseFloat(entry.books[bookmaker].over);
                  } else if (typeof entry.books[bookmaker] === 'number') {
                    value = parseFloat(entry.books[bookmaker]);
                  }
                } else {
                  // Show under odds
                  if (entry.books[bookmaker].under !== undefined) {
                    value = parseFloat(entry.books[bookmaker].under);
                  }
                }
              }

              if (value !== undefined && !isNaN(value)) {
                values.push(value);
              }
            }
          });

          // If multiple values exist (main + alt), use the best (highest) one
          if (values.length > 0) {
            return Math.max(...values);
          }
          return null;
        });

        const hasData = data.some(value => value !== null);
        if (!hasData) return null;

        // Get latest value for sorting
        let latestValue = null;
        for (let i = data.length - 1; i >= 0; i--) {
          if (data[i] !== null) {
            latestValue = data[i];
            break;
          }
        }

        // Calculate point radius
        const pointRadius = data.map((value, index) => {
          if (value === null) return 0;
          if (index === 0) return 3;
          if (index === data.length - 1) return 3;

          let prevValue = null;
          for (let i = index - 1; i >= 0; i--) {
            if (data[i] !== null) {
              prevValue = data[i];
              break;
            }
          }

          return (prevValue !== null && value !== prevValue) ? 3 : 0;
        });

        // Apply forward alias for display (e.g., williamhill_us -> caesars)
        const displayName = BOOKMAKER_ALIASES[bookmaker] || bookmaker;

        return {
          label: displayName.charAt(0).toUpperCase() + displayName.slice(1),
          data: data,
          borderColor: bookmakerColors[bookmaker] || '#999999',
          backgroundColor: bookmakerColors[bookmaker] || '#999999',
          borderWidth: 2,
          pointRadius: pointRadius,
          pointHoverRadius: 5,
          pointBackgroundColor: bookmakerColors[bookmaker] || '#999999',
          hidden: isBookEnabledForGraphs ? !isBookEnabledForGraphs(bookmaker) : false,
          latestValue: latestValue,
          spanGaps: true,
          tension: 0.1
        };
      })
      .filter(dataset => dataset !== null);

    // Sort datasets by latest value (descending)
    datasetsWithLatestValues.sort((a, b) => {
      if (a.latestValue === null && b.latestValue === null) return 0;
      if (a.latestValue === null) return 1;
      if (b.latestValue === null) return -1;
      return b.latestValue - a.latestValue;
    });

    return {
      labels,
      datasets: datasetsWithLatestValues
    };
  };

  const chartData = useMemo(() => {
    if (!show) return null;
    return prepareChartData();
  }, [show, oddsHistoryData, selectedTier, showChart, chartIsOver]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: 'white',
    layout: {
      padding: {
        bottom: 20
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          fontSize: 12
        }
      },
      title: {
        display: true,
        text: `Odds Movement - ${oddsHistoryData?.marketSearch || chartMarket}${selectedTier !== null ? ` (${chartIsOver ? 'Over' : 'Under'} ${selectedTier})` : ''}`,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            if (value === null) return null;
            return `${context.dataset.label}: ${value > 0 ? '+' : ''}${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Odds'
        },
        ticks: {
          callback: function(value) {
            return value > 0 ? `+${value}` : value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  // Get unique market categories
  const getMarketCategories = () => {
    if (!playerData?.markets) return [];
    const categories = new Set(playerData.markets.map(m => m.category || 'other'));
    // Put touchdown first, remove 'all'
    const categoriesArray = Array.from(categories);
    if (categoriesArray.includes('touchdown')) {
      return ['touchdown', ...categoriesArray.filter(c => c !== 'touchdown')];
    }
    return categoriesArray;
  };

  // Filter markets by selected type
  const getFilteredMarkets = () => {
    if (!playerData?.markets) return [];

    // Map simplified market types to actual market categories
    const marketMapping = {
      '1stlast': { category: 'firstlast' },
      'patds': { category: 'passing', subCategory: 'PaTDS' },
      'payds': { category: 'passing', subCategory: 'PaYds' },
      'ruyds': { category: 'rushing', subCategory: 'RuYds' },
      'ruatt': { category: 'rushing', subCategory: 'RuATT' },
      'reyds': { category: 'receiving', subCategory: 'ReYds' },
      'rec': { category: 'receiving', subCategory: 'Receptions' },
      'rureyds': { category: 'rushing_receiving', subCategory: 'RuReYds' },
      'fg': { category: 'kicking', subCategory: 'FG' }
    };

    const mapping = marketMapping[selectedMarketType];
    if (mapping) {
      if (mapping.subCategory) {
        return playerData.markets.filter(m =>
          m.category === mapping.category && m.subCategory === mapping.subCategory
        );
      } else {
        return playerData.markets.filter(m => m.category === mapping.category);
      }
    }

    return playerData.markets.filter(m => (m.category || 'other') === selectedMarketType);
  };

  // Format odds display
  const formatOdds = (odds) => {
    if (!odds) return '-';
    return odds > 0 ? `+${odds}` : odds;
  };


  // Get best odds for a line
  const getBestOdds = (books, type) => {
    let best = null;
    let bestBook = null;

    if (!books) {
      return { odds: best, book: bestBook };
    }

    Object.entries(books).forEach(([book, odds]) => {
      if (odds[type]) {
        if (!best || (type === 'over' ? odds[type] > best : odds[type] > best)) {
          best = odds[type];
          bestBook = book;
        }
      }
    });

    return { odds: best, book: bestBook };
  };

  // Extract data for tables
  const rushingYardsData = playerData?.markets?.find(m => m.marketKey === 'player_rush_yds')?.lines || [];
  const rushingAttemptsData = playerData?.markets?.find(m => m.marketKey === 'player_rush_attempts')?.lines || [];
  const receivingYardsData = playerData?.markets?.find(m => m.marketKey === 'player_reception_yds')?.lines || [];
  const receptionsData = playerData?.markets?.find(m => m.marketKey === 'player_receptions')?.lines || [];
  const rushRecYardsData = playerData?.markets?.find(m => m.marketKey === 'player_rush_reception_yds')?.lines || [];
  const fieldGoalsData = playerData?.markets?.find(m => m.marketKey === 'player_field_goals')?.lines || [];

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      fullscreen="sm-down"
      centered
      scrollable
      className="nfl-player-modal"
      backdropClassName="player-modal-backdrop"
    >
      {isMobile && <ModalBrandingBar />}
      <Modal.Header closeButton style={{ borderBottom: '2px solid #dee2e6' }}>
        <Modal.Title className="w-100">
          {playerData?.player && (
            <div className="d-flex align-items-center gap-3">
              <img
                src={playerData.player.photo}
                alt={playerName}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #dee2e6'
                }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/60x60?text=No+Photo';
                }}
              />
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{playerName}</h5>
                  {playerData.player.jerseyNumber && (
                    <Badge bg="secondary">#{playerData.player.jerseyNumber}</Badge>
                  )}
                </div>
                <div className="text-muted small d-flex align-items-center gap-2">
                  <img
                    src={getTeamLogoUrl(playerTeam)}
                    alt={playerTeam}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span>{playerTeam} • {playerPosition}</span>
                  <AddToScratchpadLink
                    playerData={{ gameId, playerId, playerName, playerTeam, awayTeam, homeTeam }}
                    onToggleScratchpad={onToggleScratchpad}
                    isInScratchpad={isInScratchpad}
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      {/* No inline height cap — the Modal `scrollable` prop puts the body
          in a flex column that fills the remaining space under a pinned
          Header, so the player info at the top never moves as the odds
          tables scroll. */}
      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading player markets...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {playerData && !loading && !showChart && (
          <>
            {/* Market Selection */}
            <div className="mb-3">
              <div className="d-flex gap-2 flex-wrap">
                {['touchdown', '1stlast', 'patds', 'payds', 'ruyds', 'ruatt', 'reyds', 'rec', 'rureyds', 'fg']
                  .filter(m => m !== 'fg' || String(playerPosition).toUpperCase() === 'PK')
                  .map(market => {
                  const marketLabels = {
                    touchdown: 'TDs',
                    '1stlast': '1st/Last',
                    patds: 'PaTDs',
                    payds: 'PaYds',
                    ruyds: 'RuYds',
                    ruatt: 'RuAtt',
                    reyds: 'ReYds',
                    rec: 'Rec',
                    rureyds: 'Ru+RecYds',
                    fg: 'FG'
                  };

                  // Check if player has data for this market
                  const marketMapping = {
                    'touchdown': { category: 'touchdown' },
                    '1stlast': { category: 'firstlast' },
                    'patds': { category: 'passing', subCategory: 'PaTDS' },
                    'payds': { category: 'passing', subCategory: 'PaYds' },
                    'ruyds': { category: 'rushing', subCategory: 'RuYds' },
                    'ruatt': { category: 'rushing', subCategory: 'RuATT' },
                    'reyds': { category: 'receiving', subCategory: 'ReYds' },
                    'rec': { category: 'receiving', subCategory: 'Receptions' },
                    'rureyds': { category: 'rushing_receiving', subCategory: 'RuReYds' },
                    'fg': { category: 'kicking', subCategory: 'FG' }
                  };

                  const mapping = marketMapping[market];
                  const hasData = playerData.markets?.some(m => {
                    if (mapping.subCategory) {
                      return m.category === mapping.category && m.subCategory === mapping.subCategory;
                    }
                    return m.category === mapping.category;
                  });
                  // Skill-position players see every toggle so their game-log
                  // chart stays accessible even when the book has no odds for
                  // that market.
                  const canSelect = hasData || isSkillPosition;

                  return (
                    <button
                      key={market}
                      type="button"
                      className={`btn btn-sm ${selectedMarketType === market ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => canSelect && setSelectedMarketType(market)}
                      disabled={!canSelect}
                      style={{
                        opacity: canSelect ? 1 : 0.5,
                        cursor: canSelect ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {marketLabels[market]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Markets Display */}
            {/* Passing Yards */}
            {selectedMarketType === 'payds' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Passing Stats</h6>
                {getFilteredMarkets().length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}


                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>
                        {showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}
                      </th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredMarkets().map(market => (
                      market.lines.map((line, lineIndex) => (
                        <React.Fragment key={`${market.marketKey}-${line.point}`}>
                          {showOver && (
                            <tr>
                              {showOver && showUnder ? (
                                <td rowSpan={2} style={{
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                  color: '#007bff',
                                  cursor: 'pointer'
                                }}
                                onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                                >
                                  {line.point}
                                </td>
                              ) : (
                                <td style={{
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  color: '#007bff',
                                  cursor: 'pointer'
                                }}
                                onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                                >
                                  {line.point}
                                </td>
                              )}
                          {bookmakers.map(book => {
                              const dataKey = getBookmakerDataKey(book);
                              const odds = line.books?.[dataKey]?.over;

                              // Calculate best odds and diff for tier coloring
                              const lineOdds = line.books ? getScopedBooks(line.books).map(b => b.over).filter(o => o !== null && o !== undefined) : [];
                              const bestOdds = Math.max(...lineOdds);
                              const bestOddsCount = lineOdds.filter(o => o === bestOdds).length;
                              const secondBestOdds = Math.max(...lineOdds.filter(o => o !== bestOdds));
                              // Handle cross-zero comparison (positive best odds vs negative second best)
                              let diff;
                              if (bestOdds > 0 && secondBestOdds < 0) {
                                const spreadPositive = bestOdds - 100;
                                const spreadNegative = secondBestOdds - (-100);
                                const totalSpread = spreadPositive - spreadNegative;
                                diff = Math.abs(totalSpread / secondBestOdds);
                              } else {
                                diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
                              }
                              const isBest = odds === bestOdds && bestOddsCount === 1;  // Only true if this is the sole best odds

                              // Calculate tier color styling
                              let cellStyle = {
                                cursor: odds ? 'pointer' : 'default',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                minWidth: '70px'
                              };

                              if (isBest && lineOdds.length > 1) {
                                if (diff >= 0.25) {
                                  // Tier 1: Green with yellow text
                                  Object.assign(cellStyle, {
                                    backgroundColor: 'green',
                                    color: 'yellow',
                                    borderRadius: '3px'
                                  });
                                } else if (diff >= 0.15) {
                                  // Tier 2: Amber with dark text
                                  Object.assign(cellStyle, {
                                    backgroundColor: '#F59E0B',
                                    color: '#1E40AF',
                                    borderRadius: '3px'
                                  });
                                } else if (diff >= 0.05) {
                                  // Tier 3: Blue
                                  Object.assign(cellStyle, {
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    borderRadius: '3px'
                                  });
                                } else {
                                  // Best odds but below tier threshold - use amber text
                                  Object.assign(cellStyle, {
                                    color: '#F59E0B'
                                  });
                                }
                              }

                              // Apply opacity for unavailable lines
                              if (line.books?.[dataKey]?.over_isAvailable === false) {
                                cellStyle.opacity = 0.25;
                              }

                              const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'over' };
                              return (
                                <td
                                  key={`${book}-over`}
                                  style={cellStyle}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (odds) {
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                    if (!isMobile && odds && onOddsClick) {
                                      onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'over');
                                    }
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isMobile && odds) {
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (!isMobile) {
                                      setTooltipData(null);
                                    }
                                  }}
                                >
                                  {formatOdds(odds)}
                                </td>
                              );
                            })}
                            </tr>
                          )}
                          {showUnder && (
                            <tr>
                              {!showOver && (
                                <td style={{
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  color: '#007bff',
                                  cursor: 'pointer'
                                }}
                                onClick={() => fetchOddsHistory(market.marketKey, line.point, false)}
                                >
                                  {line.point}
                                </td>
                              )}
                              {bookmakers.map(book => {
                                  const dataKey = getBookmakerDataKey(book);
                                  const odds = line.books?.[dataKey]?.under;

                                  // Calculate best odds and diff for tier coloring
                                  const lineOdds = line.books ? getScopedBooks(line.books).map(b => b.under).filter(o => o !== null && o !== undefined) : [];
                                  const bestOdds = Math.max(...lineOdds);
                                  const bestOddsCount = lineOdds.filter(o => o === bestOdds).length;
                                  const secondBestOdds = Math.max(...lineOdds.filter(o => o !== bestOdds));
                                  // Handle cross-zero comparison (positive best odds vs negative second best)
                              let diff;
                              if (bestOdds > 0 && secondBestOdds < 0) {
                                const spreadPositive = bestOdds - 100;
                                const spreadNegative = secondBestOdds - (-100);
                                const totalSpread = spreadPositive - spreadNegative;
                                diff = Math.abs(totalSpread / secondBestOdds);
                              } else {
                                diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
                              }
                                  const isBest = odds === bestOdds && bestOddsCount === 1;

                                  // Calculate tier color styling
                                  let cellStyle = {
                                    textAlign: 'center',
                                    padding: '8px',
                                    fontSize: '0.9rem',
                                    cursor: odds ? 'pointer' : 'default',
                                    fontWeight: 'bold',
                                    minWidth: '70px'
                                  };

                                  if (isBest && diff > 0) {
                                    if (diff >= 0.25) {
                                      // Tier 1: Green
                                      Object.assign(cellStyle, {
                                        backgroundColor: '#10B981',
                                        color: 'white',
                                        borderRadius: '3px'
                                      });
                                    } else if (diff >= 0.15) {
                                      // Tier 2: Amber
                                      Object.assign(cellStyle, {
                                        backgroundColor: '#F59E0B',
                                        color: '#1E40AF',
                                        borderRadius: '3px'
                                      });
                                    } else if (diff >= 0.05) {
                                      // Tier 3: Blue
                                      Object.assign(cellStyle, {
                                        backgroundColor: '#3B82F6',
                                        color: 'white',
                                        borderRadius: '3px'
                                      });
                                    } else {
                                      // Best odds but below tier threshold
                                      Object.assign(cellStyle, {
                                        color: '#F59E0B'
                                      });
                                    }
                                  }

                                  // Apply opacity for unavailable lines
                                  if (line.books?.[dataKey]?.under_isAvailable === false) {
                                    cellStyle.opacity = 0.25;
                                  }

                                  const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'under' };
                                  return (
                                    <td
                                      key={`${book}-under`}
                                      style={cellStyle}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (odds) {
                                          handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                        }
                                        if (!isMobile && odds && onOddsClick) {
                                          onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'under');
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isMobile && odds) {
                                          handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        if (!isMobile) {
                                          setTooltipData(null);
                                        }
                                      }}
                                    >
                                      {formatOdds(odds)}
                                    </td>
                                  );
                                })}
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ))}
                  </tbody>
                </Table>

              </div>
            ) : selectedMarketType === 'ruyds' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Rushing Yards</h6>
                {rushingYardsData.length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}

                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>{showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}</th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rushingYardsData.map((line, lineIndex) => (
                      <React.Fragment key={`ruYds-${line.point}`}>
                        {showOver && renderOddsRow(line, 'over', 'player_rush_yds', 'ruYds', lineIndex, { rowSpan: showUnder ? 2 : 1 })}
                        {showUnder && renderOddsRow(line, 'under', 'player_rush_yds', 'ruYds', lineIndex, showOver ? null : { rowSpan: 1 })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : selectedMarketType === 'ruatt' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Rushing Attempts</h6>
                {rushingAttemptsData.length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}

                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>{showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}</th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rushingAttemptsData.map((line, lineIndex) => (
                      <React.Fragment key={`ruAtt-${line.point}`}>
                        {showOver && renderOddsRow(line, 'over', 'player_rush_attempts', 'ruAtt', lineIndex, { rowSpan: showUnder ? 2 : 1 })}
                        {showUnder && renderOddsRow(line, 'under', 'player_rush_attempts', 'ruAtt', lineIndex, showOver ? null : { rowSpan: 1 })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : selectedMarketType === 'reyds' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Receiving Yards</h6>
                {receivingYardsData.length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}

                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>{showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}</th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {receivingYardsData.map((line, lineIndex) => (
                      <React.Fragment key={`reYds-${line.point}`}>
                        {showOver && renderOddsRow(line, 'over', 'player_reception_yds', 'reYds', lineIndex, { rowSpan: showUnder ? 2 : 1 })}
                        {showUnder && renderOddsRow(line, 'under', 'player_reception_yds', 'reYds', lineIndex, showOver ? null : { rowSpan: 1 })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : selectedMarketType === 'rec' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Receptions</h6>
                {receptionsData.length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}

                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>{showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}</th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {receptionsData.map((line, lineIndex) => (
                      <React.Fragment key={`receptions-${line.point}`}>
                        {showOver && renderOddsRow(line, 'over', 'player_receptions', 'receptions', lineIndex, { rowSpan: showUnder ? 2 : 1 })}
                        {showUnder && renderOddsRow(line, 'under', 'player_receptions', 'receptions', lineIndex, showOver ? null : { rowSpan: 1 })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : selectedMarketType === 'rureyds' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Rush + Rec Yards</h6>
                {rushRecYardsData.length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}

                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>{showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}</th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rushRecYardsData.map((line, lineIndex) => (
                      <React.Fragment key={`ruReYds-${line.point}`}>
                        {showOver && renderOddsRow(line, 'over', 'player_rush_reception_yds', 'ruReYds', lineIndex, { rowSpan: showUnder ? 2 : 1 })}
                        {showUnder && renderOddsRow(line, 'under', 'player_rush_reception_yds', 'ruReYds', lineIndex, showOver ? null : { rowSpan: 1 })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : selectedMarketType === 'fg' ? (
              <div className="mb-4">
                <h6 className="mb-2 text-primary">Field Goals Made</h6>
                {fieldGoalsData.length === 0 && (
                  <div className="text-muted small py-2">No odds available for this market.</div>
                )}

                {/* Over/Under toggles */}
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span className="text-muted small">Show:</span>
                  <button
                    className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowOver(!showOver)}
                  >
                    Over
                  </button>
                  <button
                    className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setShowUnder(!showUnder)}
                  >
                    Under
                  </button>
                </div>

                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: '20%', textAlign: 'center' }}>{showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}</th>
                      {bookmakers.map(book => (
                          <th key={book} className="text-center" style={{ minWidth: '70px' }}>
                            <img
                              src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                              alt={book === 'williamhill_us' ? 'caesars' : book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fieldGoalsData.map((line, lineIndex) => (
                      <React.Fragment key={`fg-${line.point}`}>
                        {showOver && renderOddsRow(line, 'over', 'player_field_goals', 'fg', lineIndex, { rowSpan: showUnder ? 2 : 1 })}
                        {showUnder && renderOddsRow(line, 'under', 'player_field_goals', 'fg', lineIndex, showOver ? null : { rowSpan: 1 })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <>
                {getFilteredMarkets().length === 0 && (
                  <div className="mb-4">
                    <div className="text-muted small py-2">No odds available for this market.</div>
                  </div>
                )}
                {getFilteredMarkets().map((market, index) => (
                <div key={market.marketKey} className="mb-4">
                  <h6 className="mb-2 text-primary">{market.marketName}</h6>

                  {/* Special handling for TD markets and passing TDs with table format */}
                  {(market.marketKey === 'player_main_tds' || market.marketKey === 'player_first_last_tds' || market.marketKey === 'player_pass_tds') ? (
                  <Table bordered size="sm" className="mb-0">
                    <thead>
                      <tr>
                        {/* No header for first column in all TD tables */}
                        <th style={{ width: '20%' }}></th>
                        {market.lines.map(line => (
                          <th
                            key={line.point || line.label}
                            className="text-center"
                            style={{
                              width: `${80 / market.lines.length}%`,
                              color: '#007bff',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              // Determine point value for history:
                              // - '1st' / 'last' → null (binary markets)
                              // - 0.5 (1+ TDs / Anytime TD) → null (binary market)
                              // - 1.5, 2.5, etc. (2+ TDs, 3+ TDs) → use the point value
                              let pointValue;
                              if (line.point === '1st' || line.point === 'last') {
                                pointValue = null;
                              } else if (line.point === 0.5) {
                                // Anytime TD is a binary market with null point
                                pointValue = null;
                              } else {
                                pointValue = line.point;
                              }

                              // Use actualMarketKey if available, otherwise fall back to market.marketKey
                              const apiMarketKey = line.actualMarketKey || market.marketKey;
                              fetchOddsHistory(apiMarketKey, pointValue, true);
                            }}
                          >
                            {line.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bookmakers.map(book => {
                          const dataKey = getBookmakerDataKey(book);
                          return (
                          <tr key={book}>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <img
                                src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                                alt={book === 'williamhill_us' ? 'caesars' : book}
                                style={{ height: '16px' }}
                              />
                            </td>
                            {market.lines.map(line => {
                              // Get odds based on line type
                              const isYesSide = line.point === 'anytime' || line.point === 'last' || line.point === '1st';
                              const odds = isYesSide
                                ? line.books?.[dataKey]?.yes
                                : line.books?.[dataKey]?.over;

                              // Calculate best odds for this line. Match the
                              // main odds screen / desktop panel by ignoring
                              // books that can't be primary best (novig, kalshi,
                              // circa, pinnacle, etc.) — otherwise ties on those
                              // books suppress a legitimate ESPN Bet / DK / FD
                              // highlight (e.g. Devonta Smith 1st TD).
                              const isBookEligible = (bookName) =>
                                bookName && !isExcludedFromBestBook(bookName);
                              const lineOdds = line.books
                                ? Object.entries(line.books)
                                    .filter(([bk]) => isBookEligible(bk))
                                    .map(([, b]) => (isYesSide ? b.yes : b.over))
                                    .filter(o => typeof o === 'number')
                                : [];
                              const bestOdds = lineOdds.length ? Math.max(...lineOdds) : null;
                              const secondBestOdds = lineOdds.length > 1
                                ? Math.max(...lineOdds.filter(o => o !== bestOdds))
                                : null;
                              // Handle cross-zero comparison (positive best odds vs negative second best).
                              // With < 2 eligible books there's nothing to compare so diff stays 0.
                              let diff = 0;
                              if (bestOdds !== null && secondBestOdds !== null) {
                                if (bestOdds > 0 && secondBestOdds < 0) {
                                  const spreadPositive = bestOdds - 100;
                                  const spreadNegative = secondBestOdds - (-100);
                                  const totalSpread = spreadPositive - spreadNegative;
                                  diff = Math.abs(totalSpread / secondBestOdds);
                                } else {
                                  diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
                                }
                              }
                              const bestOddsCount = lineOdds.filter(o => o === bestOdds).length;
                              // Best is only highlighted when it's uniquely best AND this book is
                              // eligible to be primary (excluded books can't earn the highlight).
                              const isBest = bestOdds !== null
                                && odds === bestOdds
                                && bestOddsCount === 1
                                && !isExcludedFromBestBook(book);

                              // Calculate tier color styling
                              let cellStyle = {
                                cursor: odds ? 'pointer' : 'default',
                                fontWeight: 'bold',
                                padding: '8px',
                                textAlign: 'center',
                                minWidth: '70px'
                              };

                              if (isBest && lineOdds.length > 1) {
                                if (diff >= 0.25) {
                                  // Tier 1: Green with yellow text
                                  Object.assign(cellStyle, {
                                    backgroundColor: 'green',
                                    color: 'yellow',
                                    borderRadius: '3px'
                                  });
                                } else if (diff >= 0.15) {
                                  // Tier 2: Amber with dark text
                                  Object.assign(cellStyle, {
                                    backgroundColor: '#F59E0B',
                                    color: '#1E40AF',
                                    borderRadius: '3px'
                                  });
                                } else if (diff >= 0.05) {
                                  // Tier 3: Blue
                                  Object.assign(cellStyle, {
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    borderRadius: '3px'
                                  });
                                } else {
                                  // Best odds but below tier threshold - use amber text
                                  Object.assign(cellStyle, {
                                    color: '#F59E0B'
                                  });
                                }
                              }

                              // Apply opacity for unavailable lines
                              const isYesType = line.point === 'anytime' || line.point === 'last' || line.point === '1st';
                              const availKey = isYesType ? 'yes_isAvailable' : 'over_isAvailable';
                              if (line.books?.[dataKey]?.[availKey] === false) {
                                cellStyle.opacity = 0.25;
                              }

                              const tdMarketKey = line.point === 'anytime' ? 'player_anytime_td'
                                : line.point === '1st' ? 'player_1st_td'
                                : line.point === 'last' ? 'player_last_td'
                                : 'player_tds_over';
                              const tdOddsType = isYesType ? 'yes' : 'over';
                              const betMeta = { gameId, playerId, playerName, marketKey: tdMarketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: tdOddsType };
                              return (
                                <td
                                  key={`${book}-${line.point}`}
                                  style={cellStyle}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (odds) {
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                    if (!isMobile && odds && onOddsClick) {
                                      onOddsClick(gameId, playerId, playerName, tdMarketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, tdOddsType);
                                    }
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isMobile && odds) {
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (!isMobile) {
                                      setTooltipData(null);
                                    }
                                  }}
                                >
                                  {formatOdds(odds)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                        })}
                    </tbody>
                  </Table>
                ) : (
                  // Regular markets
                  market.lines.map((line, lineIndex) => {
                    // Check if this is a TD market
                    const isTdMarket = line.books && Object.values(line.books)[0]?.yes !== undefined;

                    if (isTdMarket) {
                    // TD markets - display in 4 rows format (10 books total)
                    const booksArray = Object.entries(line.books);
                    const rows = [];

                    for (let i = 0; i < booksArray.length; i += 3) {
                      const rowBooks = booksArray.slice(i, i + 3);

                      // Add logo row
                      rows.push(
                        <tr key={`${market.marketKey}-${line.point}-logos-${i}`}>
                          {rowBooks.map(([book]) => (
                            <td key={`${book}-logo`} className="text-center" style={{ width: '33.33%', padding: '8px', borderBottom: 'none' }}>
                              <img
                                src={getBookLogoUrl(book === 'williamhill_us' ? 'caesars' : book)}
                                alt={book === 'williamhill_us' ? 'caesars' : book}
                                style={{ height: '16px' }}
                              />
                            </td>
                          ))}
                          {/* Add empty cells if row has less than 3 books */}
                          {rowBooks.length < 3 && [...Array(3 - rowBooks.length)].map((_, idx) => (
                            <td key={`empty-logo-${idx}`} style={{ width: '33.33%', borderBottom: 'none' }}></td>
                          ))}
                        </tr>
                      );

                      // Add odds row
                      const bestOdds = Math.max(...booksArray.map(([_, b]) => b.yes || -999999));
                      const secondBestOdds = Math.max(...booksArray.map(([_, b]) => b.yes || -999999).filter(o => o !== bestOdds));

                      // Handle cross-zero comparison (positive best odds vs negative second best)
                      let diff;
                      if (bestOdds > 0 && secondBestOdds < 0) {
                        const spreadPositive = bestOdds - 100;
                        const spreadNegative = secondBestOdds - (-100);
                        const totalSpread = spreadPositive - spreadNegative;
                        diff = Math.abs(totalSpread / secondBestOdds);
                      } else {
                        diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
                      }

                      rows.push(
                        <tr key={`${market.marketKey}-${line.point}-odds-${i}`}>
                          {rowBooks.map(([book, bookData]) => {
                            const odds = bookData.yes;
                            const allOdds = booksArray.map(([_, b]) => b.yes || -999999).filter(o => o > -999999);
                            const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
                            const isBest = odds === bestOdds && bestOddsCount === 1;  // Only true if this is the sole best odds

                            // Calculate tier color styling
                            let cellStyle = {
                              width: '33.33%',
                              minWidth: '70px',
                              cursor: odds ? 'pointer' : 'default',
                              fontWeight: 'bold',
                              padding: '8px',
                              borderTop: 'none'
                            };

                            if (isBest && booksArray.length > 1) {
                              if (diff >= 0.25) {
                                // Tier 1: Green with yellow text
                                Object.assign(cellStyle, {
                                  backgroundColor: 'green',
                                  color: 'yellow',
                                  borderRadius: '3px'
                                });
                              } else if (diff >= 0.15) {
                                // Tier 2: Amber with dark text
                                Object.assign(cellStyle, {
                                  backgroundColor: '#F59E0B',
                                  color: '#1E40AF',
                                  borderRadius: '3px'
                                });
                              } else if (diff >= 0.05) {
                                // Tier 3: Blue
                                Object.assign(cellStyle, {
                                  backgroundColor: '#3B82F6',
                                  color: 'white',
                                  borderRadius: '3px'
                                });
                              } else {
                                // Best odds but below tier threshold - use amber text
                                Object.assign(cellStyle, {
                                  color: '#F59E0B'
                                });
                              }
                            }

                            // Apply opacity for unavailable lines
                            if (bookData.yes_isAvailable === false) {
                              cellStyle.opacity = 0.25;
                            }

                            const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'yes' };
                            return (
                              <td
                                key={`${book}-odds`}
                                className="text-center"
                                style={cellStyle}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (odds) {
                                    handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                  }
                                  if (!isMobile && odds && onOddsClick) {
                                    onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'yes');
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  if (!isMobile && odds) {
                                    handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (!isMobile) {
                                    setTooltipData(null);
                                  }
                                }}
                              >
                                {formatOdds(odds)}
                              </td>
                            );
                          })}
                          {/* Add empty cells if row has less than 3 books */}
                          {rowBooks.length < 3 && [...Array(3 - rowBooks.length)].map((_, idx) => (
                            <td key={`empty-odds-${idx}`} style={{ width: '33.33%', borderTop: 'none' }}></td>
                          ))}
                        </tr>
                      );
                    }

                    return (
                      <div key={`${market.marketKey}-${line.point}`} className="mb-3">
                        {line.point && (
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-bold">{(() => {
                              const isBinary = market.marketKey && market.marketKey.includes('binary');
                              return line.point === 0.5 && isBinary ? 'Yes' : `Over ${line.point}`;
                            })()}</span>
                          </div>
                        )}
                        <Table bordered size="sm" className="mb-0">
                          <tbody>{rows}</tbody>
                        </Table>
                      </div>
                    );
                  }

                  // Regular over/under markets
                  return (
                    <div key={`${market.marketKey}-${line.point}`} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">Line: {line.point}</span>
                      </div>

                      <Table bordered size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '20%' }}>Type</th>
                            {bookmakers.map(book => (
                              <th key={book} className="text-center" style={{ width: '16%', minWidth: '70px' }}>
                                <img
                                  src={getBookLogoUrl(book)}
                                  alt={book}
                                  style={{ height: '16px' }}
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Over Row */}
                          <tr>
                            <td className="fw-bold">Over</td>
                            {bookmakers.map(book => {
                              const dataKey = getBookmakerDataKey(book);
                              const odds = line.books[dataKey]?.over;
                              const bestOver = getBestOdds(line.books, 'over');
                              const isBest = bestOver.book === book && odds === bestOver.odds;

                              return (
                                <td
                                  key={`${book}-over`}
                                  className="text-center"
                                  style={{
                                    cursor: odds ? 'pointer' : 'default',
                                    backgroundColor: isBest ? '#d4f4dd' : 'transparent',
                                    fontWeight: isBest ? 'bold' : 'normal',
                                    minWidth: '70px',
                                    opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'over' };
                                    if (odds) {
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                    if (!isMobile && odds && onOddsClick) {
                                      onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'over');
                                    }
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isMobile && odds) {
                                      const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'over' };
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (!isMobile) {
                                      setTooltipData(null);
                                    }
                                  }}
                                >
                                  {formatOdds(odds)}
                                </td>
                              );
                            })}
                          </tr>

                          {/* Under Row */}
                          <tr>
                            <td className="fw-bold">Under</td>
                            {bookmakers.map(book => {
                              const dataKey = getBookmakerDataKey(book);
                              const odds = line.books[dataKey]?.under;
                              const bestUnder = getBestOdds(line.books, 'under');
                              const isBest = bestUnder.book === book && odds === bestUnder.odds;

                              return (
                                <td
                                  key={`${book}-under`}
                                  className="text-center"
                                  style={{
                                    cursor: odds ? 'pointer' : 'default',
                                    backgroundColor: isBest ? '#d4f4dd' : 'transparent',
                                    fontWeight: isBest ? 'bold' : 'normal',
                                    minWidth: '70px',
                                    opacity: line.books[dataKey]?.under_isAvailable === false ? 0.25 : undefined
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'under' };
                                    if (odds) {
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                    if (!isMobile && odds && onOddsClick) {
                                      onOddsClick(gameId, playerName, playerTeam, market.marketKey, book, odds, line.point, 'under');
                                    }
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isMobile && odds) {
                                      const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, line: line.point, side: 'under' };
                                      handleOddsClick(e, odds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (!isMobile) {
                                      setTooltipData(null);
                                    }
                                  }}
                                >
                                  {formatOdds(odds)}
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  );
                  })
                )}
              </div>
              ))}
              </>
            )}

            {getFilteredMarkets().length === 0 && !['payds', 'ruyds', 'ruatt', 'reyds', 'rec', 'rureyds', 'fg'].includes(selectedMarketType) && (
              <div className="text-center text-muted py-4">
                No markets available for this category
              </div>
            )}

            {/* Helper text for chart functionality */}
            <div style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: '#6c757d',
              marginTop: '16px',
              padding: '8px'
            }}>
              📊 Click a point value to see odds movement
            </div>

            {/* Per-game performance chart — game log for the currently
                selected market type. Maps NFL modal's short market codes
                ('payds', 'ruyds', etc.) to the canonical `player_*` keys
                the chart config expects. */}
            {(() => {
              const gameLogMarketMap = {
                payds: 'player_pass_yds',
                patds: 'player_pass_tds',
                ruyds: 'player_rush_yds',
                ruatt: 'player_rush_attempts',
                reyds: 'player_reception_yds',
                rec: 'player_receptions',
                rureyds: 'player_rush_reception_yds',
                fg: 'player_field_goals',
                '1stlast': 'player_1st_td',
                // Touchdown tab bundles anytime + total TD lines; chart the
                // total-TDs count so every actual game TD is reflected
                // (anytime binary bars under-represent multi-TD games).
                touchdown: 'player_tds_over',
              };
              const glMarket = gameLogMarketMap[selectedMarketType];
              if (!glMarket) return null;
              // Find the first visible market that maps to this game-log
              // market — use its primary line point as the O/U reference
              // line on the chart (matches NBA).
              const filtered = getFilteredMarkets();
              const primaryMarket = filtered.find(m => m.marketKey === glMarket) || filtered[0];
              return (
                <div style={{ marginTop: '18px', backgroundColor: '#0f172a', borderRadius: '8px', padding: '10px' }}>
                  <PlayerStatsChart
                    sport="nfl"
                    playerId={playerId}
                    marketType={glMarket}
                    playerTeam={playerTeam}
                    awayTeam={awayTeam}
                    homeTeam={homeTeam}
                    lineValue={getPrimaryLinePoint(primaryMarket)}
                  />
                </div>
              );
            })()}
          </>
        )}

        {!loading && !error && showChart && (
          <>
            {/* Back Button */}
            <div className="mb-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleBackToTable}
              >
                ← Back to Odds Table
              </Button>
            </div>

            {loadingChart && (
              <div className="d-flex justify-content-center align-items-center" style={{ height: isMobile ? '70vh' : '400px' }}>
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading chart...</span>
                </Spinner>
              </div>
            )}

            {!loadingChart && chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
              <div style={{
                height: isMobile ? '70vh' : '400px',
                position: 'relative',
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <Line
                  key={`chart-${gameId}-${playerId}-${chartMarket}-${chartPoint}`}
                  data={chartData}
                  options={{
                    ...chartOptions,
                    animation: {
                      duration: 0
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        font: {
                          size: isMobile ? 14 : 16
                        }
                      },
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: isMobile ? 10 : 12,
                          fontSize: isMobile ? 11 : 12
                        }
                      }
                    }
                  }}
                  plugins={[]}
                />
              </div>
            ) : !loadingChart && (
              <div className="alert alert-warning" role="alert">
                No odds movement data available for this market.
              </div>
            )}
          </>
        )}

        {/* Odds detail — desktop floats a hover tooltip at the cursor,
            mobile renders a dismissible bottom sheet so the info doesn't
            vanish when the user's finger leaves the cell. */}
        {tooltipData && !isMobile && (
          <div
            style={{
              position: 'fixed',
              left: `${tooltipData.x + 10}px`,
              top: `${tooltipData.y + 10}px`,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 9999,
              fontSize: '0.85rem',
              minWidth: '200px',
              pointerEvents: 'none'
            }}
          >
            <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #4b5563', paddingBottom: '6px' }}>
              {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Odds:</span>
              <span style={{ fontWeight: 'bold' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Implied Prob:</span>
              <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{tooltipData.impliedProb}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Edge:</span>
              <span style={{ fontWeight: 'bold', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
              </span>
            </div>
          </div>
        )}

        {tooltipData && isMobile && (
          <div
            role="dialog"
            aria-label="Odds detail"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '14px 16px calc(16px + env(safe-area-inset-bottom))',
              boxShadow: '0 -6px 20px rgba(0,0,0,0.5)',
              borderTop: '1px solid #374151',
              borderTopLeftRadius: '14px',
              borderTopRightRadius: '14px',
              zIndex: 1210, /* above player modal (1200); toast (3000) still wins */
              fontSize: '0.9rem',
              animation: 'toastFadeUp 0.2s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
              </span>
              <button
                type="button"
                onClick={() => setTooltipData(null)}
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#d1d5db',
                  fontSize: '1.4rem',
                  lineHeight: 1,
                  padding: '4px 10px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Odds</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</div>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Implied</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#60a5fa' }}>{tooltipData.impliedProb}%</div>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Edge</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                  {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
                </div>
              </div>
            </div>

            {/* Add / Remove button — match this bet against betslipItems by
                gameId + playerId + market + line + side. Normalized because
                the main odds screen and the modal can carry playerId in
                slightly different shapes (raw `4594449` vs `nfl_4594449`)
                and line as string vs number. */}
            {(() => {
              const m = tooltipData.meta;
              if (!m) return null;
              const stripSportPrefix = (id) => String(id ?? '').replace(/^[a-z]+_/i, '');
              const normLine = (v) => {
                if (v == null || v === '') return v;
                const n = parseFloat(v);
                return Number.isFinite(n) ? n : v;
              };
              const targetPlayerId = stripSportPrefix(m.playerId);
              const targetLine = normLine(m.line);
              const existing = betslipItems.find(bet =>
                bet.gameId === m.gameId &&
                stripSportPrefix(bet.playerId) === targetPlayerId &&
                bet.market === m.marketKey &&
                normLine(bet.line) === targetLine &&
                bet.side === m.side
              );
              const btnBase = {
                width: '100%',
                marginTop: '12px',
                padding: '12px 14px',
                fontSize: '0.95rem',
                fontWeight: 700,
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                color: '#fff',
              };
              if (existing) {
                return (
                  <button
                    type="button"
                    style={{ ...btnBase, backgroundColor: '#dc2626' }}
                    onClick={() => {
                      if (onRemoveBet) onRemoveBet(existing.id);
                      setTooltipData(null);
                    }}
                  >
                    Remove from Betslip
                  </button>
                );
              }
              return (
                <button
                  type="button"
                  style={{ ...btnBase, backgroundColor: '#10b981' }}
                  onClick={() => {
                    if (onOddsClick) {
                      onOddsClick(
                        m.gameId, m.playerId, m.playerName, m.marketKey,
                        m.book, m.odds, m.awayTeam, m.homeTeam,
                        m.playerTeam, m.playerPosition, m.line, m.side
                      );
                    }
                    setTooltipData(null);
                  }}
                >
                  Add to Betslip
                </button>
              );
            })()}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NFLPlayerModal;